import { normalizarTexto } from './texto';
/**
 * Datos de Ingresos Brutos del proveedor. Espejo de backend/src/common/iibb.ts (ahí están las pruebas): la lista,
 * las etiquetas, la sugerencia de jurisdicción sede y los valores iniciales de retenciones y percepciones tienen
 * que coincidir. Son sugerencias y avisos: no cambian el cálculo de retenciones ni de percepciones.
 */

export const CONDICIONES_IIBB = ['LOCAL', 'CONVENIO_MULTILATERAL', 'EXENTO', 'NO_INSCRIPTO'] as const;
export type CondicionIibb = (typeof CONDICIONES_IIBB)[number];

export const ETIQUETAS_CONDICION_IIBB: Record<CondicionIibb, string> = {
  LOCAL: 'Local (contribuyente directo)',
  CONVENIO_MULTILATERAL: 'Convenio Multilateral',
  EXENTO: 'Exento',
  NO_INSCRIPTO: 'No inscripto',
};

export const esCondicionIibb = (v: unknown): v is CondicionIibb =>
  typeof v === 'string' && (CONDICIONES_IIBB as readonly string[]).includes(v);

/** Número de inscripción en IIBB: dígitos y guiones, de 5 a 20 caracteres, con al menos un dígito. */
export const IIBB_NUMERO_REGEX = /^(?=.*\d)[\d-]{5,20}$/;

/** Etiqueta para mostrar; null si no hay condición o el código no es de la lista. */
export const etiquetaCondicionIibb = (v: string | null | undefined): string | null =>
  esCondicionIibb(v) ? ETIQUETAS_CONDICION_IIBB[v] : null;

export const esIibbNumeroValido = (v: string): boolean => IIBB_NUMERO_REGEX.test(v);

/** Minúsculas, sin acentos, con cualquier signo como espacio y los espacios colapsados. */
const normalizar = normalizarTexto;

/**
 * Código a partir del código o de la etiqueta en castellano, sin distinguir mayúsculas ni acentos
 * (p. ej. "Convenio Multilateral" o "convenio_multilateral"). null si no es ninguno de los dos.
 */
export function parsearCondicionIibb(texto: string | null | undefined): CondicionIibb | null {
  if (!texto) return null;
  const buscado = normalizar(texto);
  if (!buscado) return null;
  return CONDICIONES_IIBB.find((c) => normalizar(c) === buscado || normalizar(ETIQUETAS_CONDICION_IIBB[c]) === buscado) ?? null;
}

export interface JurisdiccionRef {
  id: number;
  nombre: string;
  codigo: string;
  activo?: boolean;
}

/**
 * Nombres con que se escribe cada provincia (ya normalizados). El texto completo tiene que coincidir con uno:
 * "Buenos Aires / CABA" o "Tucumán y Salta" no son ninguna.
 */
const PROVINCIAS: [string, string[]][] = [
  ['caba', [
    'caba', 'c a b a', 'ciudad autonoma de buenos aires', 'ciudad autonoma buenos aires', 'ciudad de buenos aires',
    'ciudad autonoma de bs as', 'capital federal', 'cap fed', 'cdad autonoma de buenos aires',
  ]],
  ['buenos aires', ['buenos aires', 'bs as', 'bsas', 'bs aires', 'pba', 'provincia de buenos aires', 'pcia de buenos aires', 'pcia buenos aires', 'prov buenos aires', 'pcia de bs as', 'provincia de bs as']],
  ['catamarca', ['catamarca']],
  ['chaco', ['chaco']],
  ['chubut', ['chubut']],
  ['cordoba', ['cordoba']],
  ['corrientes', ['corrientes']],
  ['entre rios', ['entre rios']],
  ['formosa', ['formosa']],
  ['jujuy', ['jujuy']],
  ['la pampa', ['la pampa']],
  ['la rioja', ['la rioja']],
  ['mendoza', ['mendoza']],
  ['misiones', ['misiones']],
  ['neuquen', ['neuquen']],
  ['rio negro', ['rio negro']],
  ['salta', ['salta']],
  ['san juan', ['san juan']],
  ['san luis', ['san luis']],
  ['santa cruz', ['santa cruz']],
  ['santa fe', ['santa fe']],
  ['santiago del estero', ['santiago del estero']],
  ['tierra del fuego', ['tierra del fuego', 'tierra del fuego antartida e islas del atlantico sur']],
  ['tucuman', ['tucuman']],
];

/** Provincia a la que corresponde el texto (ya normalizado), o null. Acepta "Provincia de X" / "Pcia. X". */
function provinciaDe(normalizado: string): string | null {
  const sinPrefijo = normalizado.replace(/^(provincia|pcia|prov) (de )?/, '');
  for (const texto of [normalizado, sinPrefijo]) {
    const encontrada = PROVINCIAS.find(([, nombres]) => nombres.includes(texto));
    if (encontrada) return encontrada[0];
  }
  return null;
}

/**
 * Jurisdicción sede sugerida a partir de `provincia` (texto libre): la única jurisdicción activa cuyo nombre (o
 * código) es la misma provincia, sin distinguir mayúsculas ni acentos ("tucuman" → "Tucumán"; "CABA", "Capital
 * Federal" o "Ciudad Autónoma de Buenos Aires" → la de CABA; "Bs As" → Buenos Aires). Un texto que no es una
 * provincia conocida solo coincide con un nombre idéntico. Ninguna o más de una → null. Solo es una sugerencia.
 */
export function sugerirJurisdiccionSede(provincia: string | null | undefined, jurisdicciones: JurisdiccionRef[]): number | null {
  if (!provincia) return null;
  const texto = normalizar(provincia);
  if (!texto) return null;
  const buscada = provinciaDe(texto);
  const coincide = (j: JurisdiccionRef) => {
    if (buscada) return provinciaDe(normalizar(j.nombre)) === buscada || provinciaDe(normalizar(j.codigo)) === buscada;
    return normalizar(j.nombre) === texto;
  };
  const candidatas = jurisdicciones.filter((j) => j.activo !== false && coincide(j));
  return candidatas.length === 1 ? candidatas[0].id : null;
}

/**
 * Jurisdicción por nombre o código, sin distinguir mayúsculas ni acentos (importación). null si no hay ninguna o
 * si el texto coincide con dos jurisdicciones distintas.
 */
export function buscarJurisdiccion<J extends JurisdiccionRef>(texto: string | null | undefined, jurisdicciones: J[]): J | null {
  if (!texto) return null;
  const buscado = normalizar(texto);
  if (!buscado) return null;
  const candidatas = jurisdicciones.filter((j) => normalizar(j.nombre) === buscado || normalizar(j.codigo) === buscado);
  return candidatas.length === 1 ? candidatas[0] : null;
}

/**
 * Jurisdicción con que arranca una línea nueva de retención o percepción: la sede del proveedor si está activa y
 * ninguna otra línea la usa; si no, vacía. Es solo un valor inicial: el usuario la puede cambiar.
 */
export function jurisdiccionInicialLinea(
  jurisdiccionSedeId: number | null | undefined,
  jurisdicciones: JurisdiccionRef[],
  usadas: string[],
): string {
  if (jurisdiccionSedeId == null) return '';
  const sede = jurisdicciones.find((j) => j.id === jurisdiccionSedeId && j.activo !== false);
  if (!sede || usadas.includes(String(sede.id))) return '';
  return String(sede.id);
}

export const AVISO_PROVEEDOR_EXENTO_IIBB = 'El proveedor figura como exento de IIBB: revisá si corresponde retener';

/** Aviso al cargar retenciones de IIBB en un pago (nunca bloquea). null si no hay nada que avisar. */
export const avisoRetencionIibb = (iibbCondicion: string | null | undefined): string | null =>
  iibbCondicion === 'EXENTO' ? AVISO_PROVEEDOR_EXENTO_IIBB : null;
